import { useState } from 'react'
import * as yup from 'yup'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { validationMessages } from 'constants/validation'

import { formatDateTimeISO } from 'shared/util'

import { Checkbox, FormControlLabel, FormGroup, FormHelperText, Stack, TextField } from '@mui/material'
import { Notifications as NotificationIcon } from '@mui/icons-material'

import { Section } from 'components/controls/section'
import { ImageFileInput } from 'components/controls/imageFileInput'
import { MarkdownInput } from 'components/controls/markDownInput'

import { NotificationFormData, NotificationResult, useGetNotificationLogo } from "interface/notification"

/*
import { JSX } from 'react'
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import ja from 'dayjs/locale/ja'
import dayjs from 'dayjs'
*/

const ifEmptyToNull = (value: string | null | undefined): string | null => {
	return (value && value.length > 0) ? value : null
}

const {
  Required,
  TooLongLength,
  TooShortLength
} = validationMessages

const formSchema = yup.object().shape({
	isGlobal: yup.boolean(),
	category: yup.string().required(Required),
	title: yup.string().required(Required)
		.min(10, TooShortLength(10))
		.max(200, TooLongLength(200)),
	description: yup.string().nullable(),
	url: yup.string().nullable(),
	isOpen: yup.boolean(),
	publishStartTime: yup.date().nullable(),
	publishCloseTime: yup.date().nullable()
})

type useNotificationFormProps = {
	notification?: NotificationResult
	onSubmit: (body: { form: NotificationFormData, logo?: File }) => void
}
export const useNotificationForm = ({
	notification,
	onSubmit
}: useNotificationFormProps) => {
  const logoState = useGetNotificationLogo(notification?.notificationId)
  const [logoFile, setLogoFile] = useState<File | undefined>(undefined)
	const form = useForm({
		defaultValues: notification ? {
			isGlobal: notification.isGrobal,
			category: notification.category ?? 'info',
			title: notification.title ?? '',
			description: notification.description ?? '',
			url: notification.url ?? '',
			isOpen: notification.isOpen,
			publishStartTime: notification.publishStartTime ? new Date(notification.publishStartTime) : undefined,
			publishCloseTime: notification.publishCloseTime ? new Date(notification.publishCloseTime) : undefined
		} : {
			isGlobal: false,
			category: 'info',
			title: '',
			description: '',
			url: '',
			isOpen: false
		},
		mode: 'onChange',
		resolver: yupResolver(formSchema)
	})
	return {
		...form,
		logo: {
			state: logoState,
			file: logoFile,
			set: setLogoFile
		},
    submit: form.handleSubmit(input => onSubmit({
			form: {
				isGrobal: input.isGlobal ?? false,
				category: input.category ?? 'info',
				title: input.title ?? '',
				description: ifEmptyToNull(input.description),
				url: ifEmptyToNull(input.url),
				isOpen: input.isOpen ?? false,
				publishStartTime: formatDateTimeISO(input.publishStartTime ?? null),
				publishCloseTime: formatDateTimeISO(input.publishCloseTime ?? null),
				updateTime: notification?.updateTime
			},
			logo: logoFile
		}))
	}
}

export const NotificationForm = ({
	control,
	formState: { errors },
	logo
}: ReturnType<typeof useNotificationForm>) => {
	return (
		<Stack direction="row" spacing={2}>
			<Stack direction="column" spacing={1} width="200px" justifyContent="center">
				<ImageFileInput
					state={logo.state}
					onChangeFile={logo.set}
					emptyImage={<NotificationIcon fontSize='large'/>}
					alt="ロゴ画像"
				/>
			</Stack>

			<Stack direction="column" flexGrow={1}>
				<Section label="タイトル" labelProps={{ py: 2 }}>
					<Controller
						control={control}
						name="title"
						render={({ field }) => (
							<TextField
								{...field}
								variant="outlined"
								size="small"
								error={!!errors.title}
								helperText={errors.title ? errors.title.message : ''}
							/>
						)}
					/>
				</Section>

				<Section label="概要" labelProps={{ py: 2 }}>
					<Controller
						control={control}
						name="description"
						render={({ field }) => (
							<MarkdownInput
								{...field}
								variant="outlined"
								size="small"
								rows={10}
								error={!!errors.description}
								helperText={errors.description ? errors.description.message : ''}
							/>
						)}
					/>
				</Section>

				<Section label="リンク" labelProps={{ py: 2 }}>
					<Controller
						control={control}
						name="url"
						render={({ field }) => (
							<TextField
								{...field}
								variant="outlined"
								size="small"
								error={!!errors.url}
								helperText={errors.url ? errors.url.message : ''}
							/>
						)}
					/>
				</Section>
				
				<Section label="公開範囲" labelProps={{ py: 2 }} disablePaddingY>
					<Stack direction="row" spacing={2} alignItems="center">
						<Controller
							name="isGlobal"
							control={control}
							render={({ field, formState: { errors } }) => (
								<FormGroup {...field}>
									<FormControlLabel
										control={<Checkbox checked={field.value}/>}
										label="全テナント共通"
									/>
									<FormHelperText>{errors.isGlobal?.message || ''}</FormHelperText>
								</FormGroup>
							)}
						/>
						<Controller
							name="isOpen"
							control={control}
							render={({ field, formState: { errors } }) => (
								<FormGroup {...field}>
									<FormControlLabel
										control={<Checkbox checked={field.value} />}
										label="ログイン画面に表示"
									/>
									<FormHelperText>{errors.isOpen?.message || ''}</FormHelperText>
								</FormGroup>
							)}
						/>
					</Stack>
				</Section>

				{/*
				<Section label="公開期間" labelProps={{ py: 2 }} disablePaddingY>
					<Controller
						name="publishStartTime"
						control={control}
						rules={{
							validate: (value) => {
								const formatDate = dayjs(value).format('YYYY/MM/DD')
								if (!dayjs(formatDate).isValid()) {
									return '日付形式が間違っています'
								}
							}
						}}
						render={({ field, formState: { errors } }) => (
							<LocalizationProvider
								dateAdapter={AdapterDayjs}
								adapterLocale={ja}
							>
								<DesktopDatePicker
									{...field}
									disableMaskedInput
									inputFormat={'YYYY/MM/DD'}
									value={field.value || ''}
									renderInput={(
										params: JSX.IntrinsicAttributes & TextFieldProps
									) => (
										<TextField
											{...params}
											inputProps={{
												...params.inputProps,
												placeholder: dayjs().format('YYYY/M/DD')
											}}
											label="Date"
											error={errors.publishStartTime ? true : false}
											helperText={errors.publishStartTime?.message as string}
										/>
									)}
								/>
							</LocalizationProvider>
						)}
					/>
				</Section>
				*/}
			</Stack>
		</Stack>
	)
}